import { Driver } from '../../domain/driver/Driver';
import { TableColumn, parseDefaultTableColumns } from '../shared/tableColumns';

export const driversTableColumnKeys = [
  // `icon`,
  `label`,
  `alerts`,
  `survey`,
  `status`,
  `stops`,
  `start_location`,
  `end_location`,
  `time_window`,
  `duration`,
  `total_distance`,
  `distance_traveled`,
  `max_services`,
  `max_distance`,
  `min_distance`,
  `max_time`,
  `min_time`,
  `assigned_weight`,
  `max_weight`,
  `assigned_volume`,
  `max_volume`,
  `provides`,
  `external_id`,
  `plate`,
  `comments`,
  `email`,
  `phone`,
  `total_price`,
  `start_date`,
  `planned_start_time`,
  `end_date`,
  `planned_end_time`,
  // `custom_fields`,
  `created_at`,
] as const;

export type DriversTableColumnKeys = (typeof driversTableColumnKeys)[number];

export type DriversTableColumn = TableColumn<DriversTableColumnKeys, Driver>;

export const DRIVERS_DEFAULT_TABLE_COLUMNS: Array<DriversTableColumn> = [
  // icon: false,
  {
    identifier: `label`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `alerts`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `survey`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `status`,
    type: `string`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `stops`,
    type: `number`,
    displayable: true,
    filterable: true,
    sortable: true,
    sort: (a: Driver, b: Driver) => {
      const aValue = (a.stops ?? []).length;
      const bValue = (b.stops ?? []).length;

      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    },
  },
  {
    identifier: `start_location`,
    type: `Location`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `end_location`,
    type: `Location`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `time_window`,
    type: `Array`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `duration`,
    type: `number`,
    displayable: true,
    filterable: true,
    sortable: true,
    sort: (a: Driver, b: Driver) => {
      let aValue;
      let bValue;
      if (a.planned_end_time !== undefined && a.planned_start_time !== undefined) {
        aValue = a.planned_end_time - a.planned_start_time;
      } else {
        aValue = 0;
      }
      if (b.planned_end_time !== undefined && b.planned_start_time !== undefined) {
        bValue = b.planned_end_time - b.planned_start_time;
      } else {
        bValue = 0;
      }

      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    },
  },
  {
    identifier: `total_distance`,
    type: `number`,
    displayable: true,
    filterable: true,
    sortable: true,
    sort: (a: Driver, b: Driver) => {
      const aValue = a.getTotalDistance();
      const bValue = b.getTotalDistance();

      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    },
  },
  {
    identifier: `distance_traveled`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `max_services`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `max_distance`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `min_distance`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `max_time`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `min_time`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `assigned_weight`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
    sort: (a: Driver, b: Driver) => {
      const aValue = a.getAssignedWeight();
      const bValue = b.getAssignedWeight();

      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    },
  },
  {
    identifier: `max_weight`,
    type: `number`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `assigned_volume`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
    sort: (a: Driver, b: Driver) => {
      const aValue = a.getAssignedVolume();
      const bValue = b.getAssignedVolume();

      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    },
  },
  {
    identifier: `max_volume`,
    type: `number`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `provides`,
    type: `Array`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `external_id`,
    type: `string`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `plate`,
    type: `string`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `comments`,
    type: `string`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `email`,
    type: `string`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `phone`,
    type: `string`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `total_price`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
    sort: (a: Driver, b: Driver) => {
      const aValue = a.getTotalPriceUsingKM();
      const bValue = b.getTotalPriceUsingKM();

      // return aValue - bValue;

      if (aValue < bValue) {
        return -1;
      }
      if (aValue > bValue) {
        return 1;
      }
      return 0;
    },
  },
  {
    identifier: `start_date`,
    type: `Date`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `planned_start_time`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `end_date`,
    type: `Date`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  {
    identifier: `planned_end_time`,
    type: `number`,
    displayable: false,
    filterable: true,
    sortable: true,
  },
  // custom_fields: false,
  {
    identifier: `created_at`,
    type: `Date`,
    displayable: true,
    filterable: true,
    sortable: true,
  },
];

export const getDriversTableColumns = (): Array<DriversTableColumn> => {
  const storedTableColumns = localStorage.getItem(`DRIVERS_TABLE_COLUMNS`);
  if (!storedTableColumns) return DRIVERS_DEFAULT_TABLE_COLUMNS;

  const parsedTableColumns = JSON.parse(storedTableColumns);

  // Final table columns should have the current ones.
  const finalTableColumns: Array<DriversTableColumn> = [
    ...(Array.isArray(parsedTableColumns) ? parsedTableColumns : []),
  ];

  return parseDefaultTableColumns({
    finalTableColumns,
    defaultTableColumns: DRIVERS_DEFAULT_TABLE_COLUMNS,
  });
};

export const setDriversTableColumns = (tableColumns: Array<DriversTableColumn>) => {
  localStorage.setItem(`DRIVERS_TABLE_COLUMNS`, JSON.stringify(tableColumns));
};
